import type { CSSProperties } from "react";

import { parseCssUnitValue } from "../cssUnits.ts";
import type { ResizeHandle } from "../resizeDelta.ts";
import { getOwnLinearMatrix, getTransformOriginFractions } from "../transformMatrix.ts";
import {
  computePositionUpdate,
  computeResizeInsetAdjustment,
  getComputedInset,
  type ComputePositionUpdateParams,
  type ComputeResizeInsetAdjustmentParams,
} from "./insets.ts";
import type { InsetProperty, NumericInsets, OffsetOrigin } from "./types.ts";

/** Format numeric insets as rounded `${n}px` CSS strings. */
export const toPxInsets = (numeric: NumericInsets): Partial<Pick<CSSProperties, InsetProperty>> => {
  const out: Partial<Pick<CSSProperties, InsetProperty>> = {};
  for (const key of Object.keys(numeric) as InsetProperty[]) {
    const value = numeric[key];
    if (value !== undefined) out[key] = `${Math.round(value)}px`;
  }
  return out;
};

/** Negates the numeric part of a CSS length value. E.g. "20px" → "-20px", "-5%" → "5%". */
export const negateCssValue = (value: string | undefined): string | undefined => {
  if (value === undefined) return undefined;
  const { value: num, unit } = parseCssUnitValue(value);
  if (num === 0) return value;
  return `${-num}${unit}`;
};

/**
 * Computes inset CSS properties needed when resizing an absolutely positioned
 * element. Wraps the numeric `computeResizeInsetAdjustment` with `${n}px`
 * formatting. Returns undefined when no inset adjustment is needed.
 */
export const getResizeInsetAdjustment = (
  params: ComputeResizeInsetAdjustmentParams,
): Partial<CSSProperties> | undefined => {
  const numeric = computeResizeInsetAdjustment(params);
  return numeric ? toPxInsets(numeric) : undefined;
};

export interface GetElementResizeInsetAdjustmentParams {
  element: HTMLElement;
  offsetOrigin: OffsetOrigin | undefined;
  handle: ResizeHandle;
  widthDelta: number;
  heightDelta: number;
}

/**
 * High-level wrapper that reads the element's own linear matrix, transform
 * origin, and computed inset, then calls `getResizeInsetAdjustment`. Use this
 * from designer-side resize handlers — it owns the geometry orchestration so
 * consumers don't repeat the recipe.
 *
 * Uses the element's *own* linear matrix (not the cumulative one): ancestor
 * transforms like canvas zoom move the element and its CSS layout uniformly
 * and cancel out of the pivot-drift formula.
 */
export const getElementResizeInsetAdjustment = ({
  element,
  offsetOrigin,
  handle,
  widthDelta,
  heightDelta,
}: GetElementResizeInsetAdjustmentParams): Partial<CSSProperties> | undefined => {
  if (!offsetOrigin) return undefined;
  return getResizeInsetAdjustment({
    offsetOrigin,
    handle,
    widthDelta,
    heightDelta,
    computedInset: getComputedInset(element),
    // DOMMatrixReadOnly is structurally compatible with LinearMatrix.
    linearMatrix: getOwnLinearMatrix(element),
    transformOrigin: getTransformOriginFractions(element),
  });
};

/**
 * Computes the CSS property updates for a drag delta, respecting the active
 * offset origin. Wraps the numeric `computePositionUpdate` with `${n}px`
 * formatting. The browser-computed inset values resolve non-px units (%, em,
 * calc) to clean pixels on first drag.
 */
export const getPositionUpdateForDragDelta = (params: ComputePositionUpdateParams): Partial<CSSProperties> =>
  toPxInsets(computePositionUpdate(params));
